import {
  type AutomaticStreakStatus,
  type StreakStatusResponse,
  AUTOMATIC_STREAK_STATUS,
  UneligibleTradeReason, // Re-export shared stop reason utilities from @cede/types
  getStopReasonLabel,
  getLastTradeError,
  getFailedTradeIndex,
} from "@cede/types";

// Re-export for convenience
export { getStopReasonLabel, getLastTradeError, getFailedTradeIndex };

/**
 * Manual-trade blockers under which autopilot is still allowed. This is the
 * *allow-list* read by the autopilot guard — every reason listed here is one
 * we explicitly want to bypass, not block on.
 *
 * All currently listed reasons are volume-saturation cases: the current epoch
 * is full for manual trades, but the user can still queue autopilot up for the
 * next one. Coming-soon (FUTURE) campaigns never reach this guard — they're
 * classified as accessible directly in `useTradeCampaignAccess`.
 */
const MANUAL_BLOCKERS_ALLOWING_AUTOPILOT: readonly UneligibleTradeReason[] = [
  UneligibleTradeReason.MAX_USER_VOLUME_BY_EPOCH_REACHED,
  UneligibleTradeReason.COOLDOWN_VOLUME_REACHED,
  UneligibleTradeReason.MAX_TOTAL_VOLUME_BY_EPOCH_REACHED,
  UneligibleTradeReason.LIMIT_REACHED,
] as const;

/**
 * Returns true when the manual-trade blocker should NOT prevent the user from
 * reaching autopilot (volume saturation, not-yet-started campaign, …).
 */
export function isManualBlockerAllowingAutopilot(reason: UneligibleTradeReason | undefined | null): boolean {
  if (!reason) return false;
  return MANUAL_BLOCKERS_ALLOWING_AUTOPILOT.includes(reason);
}

/**
 * Status configuration for automatic streak display
 */
export interface AutomaticStreakStatusConfig {
  label: string;
  color: string;
  bgColor: string;
  dotColor?: string;
}

export const AUTOMATIC_STREAK_STATUS_CONFIG: Record<AutomaticStreakStatus, AutomaticStreakStatusConfig> = {
  [AUTOMATIC_STREAK_STATUS.PENDING]: {
    label: "In Progress",
    color: "text-cede-primary",
    bgColor: "bg-cede-primary/20",
    dotColor: "bg-cede-primary animate-pulse",
  },
  [AUTOMATIC_STREAK_STATUS.IN_PROGRESS]: {
    label: "In Progress",
    color: "text-cede-primary",
    bgColor: "bg-cede-primary/20",
    dotColor: "bg-cede-primary animate-pulse",
  },
  [AUTOMATIC_STREAK_STATUS.COMPLETED]: {
    label: "Completed",
    color: "text-cede-success",
    bgColor: "bg-cede-success/20",
    dotColor: "bg-cede-success",
  },
  [AUTOMATIC_STREAK_STATUS.STOPPED]: {
    label: "Stopped",
    color: "text-cede-warning",
    bgColor: "bg-cede-warning/20",
    dotColor: "bg-cede-warning",
  },
  [AUTOMATIC_STREAK_STATUS.CANCELLED]: {
    label: "Cancelled",
    color: "text-cede-text-secondary",
    bgColor: "bg-cede-text-secondary/20",
    dotColor: "bg-cede-text-secondary",
  },
};

const DEFAULT_STATUS_CONFIG: AutomaticStreakStatusConfig = {
  label: "Unknown",
  color: "text-cede-text-secondary",
  bgColor: "bg-cede-text-secondary/20",
  dotColor: "bg-cede-text-secondary",
};

export const getStatusConfig = (status: AutomaticStreakStatus): AutomaticStreakStatusConfig => {
  return AUTOMATIC_STREAK_STATUS_CONFIG[status] ?? DEFAULT_STATUS_CONFIG;
};

/**
 * Check if a streak is in an active state (can still execute trades)
 */
export const isStreakActive = (status: AutomaticStreakStatus): boolean => {
  return status === AUTOMATIC_STREAK_STATUS.PENDING || status === AUTOMATIC_STREAK_STATUS.IN_PROGRESS;
};

/**
 * Check if a streak can be cancelled
 */
export const isStreakCancellable = (status: AutomaticStreakStatus): boolean => {
  return isStreakActive(status);
};

/**
 * Calculate when the next trade will execute
 * Returns null for completed/stopped/cancelled streaks
 *
 * Uses anchor-based timing: scheduledStartTime + (cycleNumber * interval)
 * This accounts for both completed trades AND skipped cycles.
 */
export const getNextTradeTime = (streak: StreakStatusResponse): Date | null => {
  // Don't show countdown for terminal states
  if (
    streak.status === AUTOMATIC_STREAK_STATUS.COMPLETED ||
    streak.status === AUTOMATIC_STREAK_STATUS.STOPPED ||
    streak.status === AUTOMATIC_STREAK_STATUS.CANCELLED
  ) {
    return null;
  }

  // Calculate using anchor-based timing
  // Total processed cycles = completedTrades + skippedCycles
  // Next cycle number = processedCycles + 1 (1-indexed)
  const processedCycles = streak.completedTrades + (streak.skippedCycles ?? 0);
  const nextCycleNumber = processedCycles + 1;

  const startTime = new Date(streak.scheduledStartTime).getTime();
  return new Date(startTime + nextCycleNumber * streak.intervalSeconds * 1000);
};

/**
 * Get the epoch start time for a streak (the scheduledStartTime)
 * This is different from getNextTradeTime which adds the interval for the first trade
 * Use this when displaying "Next epoch starts in" countdown
 */
export const getEpochStartTime = (streak: StreakStatusResponse): Date | null => {
  // Don't show countdown for terminal states
  if (
    streak.status === AUTOMATIC_STREAK_STATUS.COMPLETED ||
    streak.status === AUTOMATIC_STREAK_STATUS.STOPPED ||
    streak.status === AUTOMATIC_STREAK_STATUS.CANCELLED
  ) {
    return null;
  }

  return new Date(streak.scheduledStartTime);
};

/**
 * Format a streak date for display
 */
export const formatStreakDate = (date: string | Date): string => {
  return (date instanceof Date ? date : new Date(date)).toLocaleString();
};

/**
 * Format a streak date with "Today" prefix if applicable
 */
export const formatStreakDateRelative = (dateString: string | Date): string => {
  const date = dateString instanceof Date ? dateString : new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return `Today ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }
  return date.toLocaleDateString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

/**
 * Format duration in seconds to human readable format
 * For short durations (< 60s): "45s"
 * For medium durations (< 1h): "5m 30s" or "5m"
 * For long durations (>= 1h): "1h 48m"
 */
export const formatStreakDuration = (totalSeconds: number): string => {
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    // For long durations, don't show seconds
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  }

  // For medium durations (minutes only)
  if (seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

/**
 * Format countdown for next trade/cycle
 * For short durations: show seconds
 * For longer durations: show human-friendly format
 */
export const formatCountdown = (totalSeconds: number): string => {
  if (totalSeconds <= 120) {
    // Under 2 minutes, show seconds
    return `${totalSeconds}s`;
  }
  return formatStreakDuration(totalSeconds);
};

/**
 * Format timeframe in milliseconds to human readable format
 * @param timeframeMs - Timeframe in milliseconds (null for epoch-based)
 * @returns Human readable timeframe string
 */
export const formatTimeframe = (timeframeMs: number | null): string => {
  if (timeframeMs === null) {
    return "Full epoch";
  }

  const totalMinutes = Math.round(timeframeMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes} min`;
  }

  if (hours === 1 && minutes === 0) {
    return "1 hour";
  }

  if (hours < 24) {
    if (minutes === 0) {
      return `${hours} hours`;
    }
    return `${hours}h ${minutes}m`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (days === 1 && remainingHours === 0) {
    return "1 day";
  }

  if (remainingHours === 0) {
    return `${days} days`;
  }

  return `${days}d ${remainingHours}h`;
};
